import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from "react";
import WhatWeOffer from "./WhatWeOffer";
import FeaturesGrid from "./FeaturesGrid";
import binifyOrangeImage from "@assets/img/binify-orange-big.png";

import "./OfferFeaturesJourney.sass";

const MOBILE_BREAKPOINT = 1023;
const MOBILE_ENTRY_START = 0.95;
const MOBILE_ENTRY_END = 0.42;
const MOBILE_HANDOFF_START = 0.015;
const MOBILE_HANDOFF_RETURN = 0.001;

const LOCAL_SHIELD_CLASS =
  "offer-features-journey__shared-shield--local";

const useSafeLayoutEffect =
  typeof window !== "undefined"
    ? useLayoutEffect
    : useEffect;

const clamp = (value, min, max) => {
  return Math.min(Math.max(value, min), max);
};

const interpolate = (start, end, progress) => {
  return start + (end - start) * progress;
};

const easeInOutCubic = (progress) => {
  if (progress < 0.5) {
    return 4 * progress * progress * progress;
  }

  return 1 - Math.pow(-2 * progress + 2, 3) / 2;
};

const createRectSnapshot = (rect) => ({
  left: rect.left,
  top: rect.top,
  width: rect.width,
  height: rect.height,
});

const roundPixel = (value) => {
  return Math.round(value * 100) / 100;
};

const OfferFeaturesJourney = () => {
  const journeyRef = useRef(null);
  const sharedShieldRef = useRef(null);

  const sourceShieldAnchorRef = useRef(null);
  const featuresElementsRef = useRef(null);

  const lockedSourceRectRef = useRef(null);
  const shieldModeRef = useRef("journey");

  const animationFrameRef = useRef(null);
  const requestUpdateRef = useRef(null);

  const handleShieldElementChange = useCallback(
    (element) => {
      sourceShieldAnchorRef.current = element;
      lockedSourceRectRef.current = null;

      requestUpdateRef.current?.();
    },
    [],
  );

  const handleFeaturesElementsChange = useCallback(
    (elements) => {
      featuresElementsRef.current = elements;

      requestUpdateRef.current?.();
    },
    [],
  );

  useSafeLayoutEffect(() => {
    const isMobileLayout = () => {
      return window.innerWidth <= MOBILE_BREAKPOINT;
    };

    const clearInlineShieldStyles = (
      sharedShield,
    ) => {
      sharedShield.style.width = "";
      sharedShield.style.height = "";
      sharedShield.style.opacity = "";
      sharedShield.style.visibility = "";
      sharedShield.style.transform = "";
    };

    const attachShieldToSource = () => {
      const sharedShield =
        sharedShieldRef.current;

      const sourceAnchor =
        sourceShieldAnchorRef.current;

      if (!sharedShield || !sourceAnchor) {
        return false;
      }

      if (
        sharedShield.parentElement !==
        sourceAnchor
      ) {
        sourceAnchor.appendChild(
          sharedShield,
        );
      }

      clearInlineShieldStyles(
        sharedShield,
      );

      sharedShield.classList.add(
        LOCAL_SHIELD_CLASS,
      );

      shieldModeRef.current = "source";

      return true;
    };

    const attachShieldToJourney = () => {
      const journey = journeyRef.current;
      const sharedShield =
        sharedShieldRef.current;

      if (!journey || !sharedShield) {
        return false;
      }

      if (
        sharedShield.parentElement !== journey
      ) {
        journey.appendChild(sharedShield);
      }

      sharedShield.classList.remove(
        LOCAL_SHIELD_CLASS,
      );

      shieldModeRef.current = "journey";

      return true;
    };

    const hideSharedShield = () => {
      const sharedShield =
        sharedShieldRef.current;

      if (!sharedShield) return;

      sharedShield.style.opacity = "0";
      sharedShield.style.visibility =
        "hidden";
    };

    const showSharedShield = ({
      left,
      top,
      width,
      height,
    }) => {
      const sharedShield =
        sharedShieldRef.current;

      if (!sharedShield) return;

      sharedShield.style.width =
        `${roundPixel(width)}px`;

      sharedShield.style.height =
        `${roundPixel(height)}px`;

      sharedShield.style.transform =
        `translate3d(
          ${roundPixel(left)}px,
          ${roundPixel(top)}px,
          0
        )`;

      sharedShield.style.opacity = "1";
      sharedShield.style.visibility =
        "visible";
    };

    const updateSharedShield = () => {
      const journey = journeyRef.current;
      const sharedShield =
        sharedShieldRef.current;

      const sourceAnchor =
        sourceShieldAnchorRef.current;

      if (
        !journey ||
        !sharedShield ||
        !sourceAnchor
      ) {
        hideSharedShield();
        return;
      }

      const viewportWidth =
        window.innerWidth;

      const viewportHeight =
        window.innerHeight;

      const mobileLayout =
        isMobileLayout();

      const journeyRect =
        journey.getBoundingClientRect();

      const currentSourceRect =
        sourceAnchor.getBoundingClientRect();

      const featuresElements =
        featuresElementsRef.current;

      if (
        mobileLayout &&
        (
          !featuresElements?.track ||
          !featuresElements?.sticky ||
          !featuresElements?.shieldTarget
        )
      ) {
        lockedSourceRectRef.current = null;

        attachShieldToSource();
        return;
      }

      let left = currentSourceRect.left;
      let top = currentSourceRect.top;
      let width = currentSourceRect.width;
      let height = currentSourceRect.height;

      if (
        mobileLayout &&
        featuresElements?.track &&
        featuresElements?.sticky &&
        featuresElements?.shieldTarget
      ) {
        const featuresTrackRect =
          featuresElements.track
            .getBoundingClientRect();

        const stickyRect =
          featuresElements.sticky
            .getBoundingClientRect();

        const contentRect =
          featuresElements.content
            ?.getBoundingClientRect();

        const targetRect =
          featuresElements.shieldTarget
            .getBoundingClientRect();

        const transitionStart =
          viewportHeight *
          MOBILE_ENTRY_START;

        const transitionEnd =
          viewportHeight *
          MOBILE_ENTRY_END;

        const rawProgress = clamp(
          (
            transitionStart -
            featuresTrackRect.top
          ) /
            (
              transitionStart -
              transitionEnd
            ),
          0,
          1,
        );

        const currentlyInJourney =
          shieldModeRef.current ===
          "journey";

        const shouldStayInSource =
          currentlyInJourney
            ? rawProgress <=
              MOBILE_HANDOFF_RETURN
            : rawProgress <
              MOBILE_HANDOFF_START;

        if (shouldStayInSource) {
          lockedSourceRectRef.current = null;

          attachShieldToSource();
          return;
        }

        if (!lockedSourceRectRef.current) {
          const visibleShieldRect =
            sharedShield.getBoundingClientRect();

          const hasVisibleShieldSize =
            visibleShieldRect.width > 0 &&
            visibleShieldRect.height > 0;

          lockedSourceRectRef.current =
            createRectSnapshot(
              hasVisibleShieldSize
                ? visibleShieldRect
                : currentSourceRect,
            );
        }

        attachShieldToJourney();

        const sourceRect =
          lockedSourceRectRef.current;

        const transitionProgress =
          easeInOutCubic(rawProgress);

        const targetWidth =
          targetRect.width ||
          sourceRect.width;

        const targetHeight =
          targetRect.height ||
          sourceRect.height;

        const targetLeft =
          viewportWidth / 2 -
          targetWidth / 2;

        const targetOffsetInsideContent =
          contentRect
            ? targetRect.top -
              contentRect.top
            : 0;

        const finalContentTop =
          contentRect
            ? stickyRect.top +
              (
                stickyRect.height -
                contentRect.height
              ) /
                2
            : targetRect.top;

        const targetTop =
          finalContentTop +
          targetOffsetInsideContent;

        left = interpolate(
          sourceRect.left,
          targetLeft,
          transitionProgress,
        );

        top = interpolate(
          sourceRect.top,
          targetTop,
          transitionProgress,
        );

        width = interpolate(
          sourceRect.width,
          targetWidth,
          transitionProgress,
        );

        height = interpolate(
          sourceRect.height,
          targetHeight,
          transitionProgress,
        );
      } else {
        lockedSourceRectRef.current = null;

        attachShieldToJourney();

        left = currentSourceRect.left;
        top = currentSourceRect.top;
        width = currentSourceRect.width;
        height = currentSourceRect.height;
      }

      const journeyIsVisible =
        journeyRect.bottom > 0 &&
        journeyRect.top < viewportHeight;

      const shieldIsNearViewport =
        top + height > -viewportHeight &&
        top < viewportHeight * 1.5;

      const hasValidSize =
        width > 0 &&
        height > 0;

      if (
        !journeyIsVisible ||
        !shieldIsNearViewport ||
        !hasValidSize
      ) {
        hideSharedShield();
        return;
      }

      showSharedShield({
        left,
        top,
        width,
        height,
      });
    };

    const requestUpdate = () => {
      if (
        animationFrameRef.current !== null
      ) {
        return;
      }

      animationFrameRef.current =
        window.requestAnimationFrame(() => {
          animationFrameRef.current = null;
          updateSharedShield();
        });
    };

    requestUpdateRef.current =
      requestUpdate;

    window.addEventListener(
      "scroll",
      requestUpdate,
      {
        passive: true,
      },
    );

    window.addEventListener(
      "resize",
      requestUpdate,
    );

    requestUpdate();

    return () => {
      window.removeEventListener(
        "scroll",
        requestUpdate,
      );

      window.removeEventListener(
        "resize",
        requestUpdate,
      );

      requestUpdateRef.current = null;
      lockedSourceRectRef.current = null;

      if (
        animationFrameRef.current !== null
      ) {
        window.cancelAnimationFrame(
          animationFrameRef.current,
        );
      }

      const journey = journeyRef.current;
      const sharedShield =
        sharedShieldRef.current;

      if (journey && sharedShield) {
        if (
          sharedShield.parentElement !==
          journey
        ) {
          journey.appendChild(
            sharedShield,
          );
        }

        sharedShield.classList.remove(
          LOCAL_SHIELD_CLASS,
        );

        clearInlineShieldStyles(
          sharedShield,
        );
      }

      shieldModeRef.current = "journey";
    };
  }, []);

  return (
    <div
      className="offer-features-journey"
      ref={journeyRef}
    >
      <WhatWeOffer
        onShieldElementChange={
          handleShieldElementChange
        }
      />

      <FeaturesGrid
        onMobileElementsChange={
          handleFeaturesElementsChange
        }
      />

      <img
        ref={sharedShieldRef}
        src={binifyOrangeImage}
        className="offer-features-journey__shared-shield"
        alt="Binify Security Shield"
        aria-hidden="true"
        draggable="false"
      />
    </div>
  );
};

export default OfferFeaturesJourney;
